import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Cake, Send, RotateCcw } from "lucide-react";

const TOKEN = "{first_name}";

function firstName(user) {
    return (user?.first_name || (user?.name || "").split(" ")[0] || "Riley").trim();
}

export default function BirthdaySmsEditor() {
    const { user } = useAuth();
    const [cfg, setCfg] = useState(null);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        api.get("/admin/sms-templates")
            .then(({ data }) => { setCfg(data); setText(data.birthday); })
            .catch(() => toast.error("Failed to load birthday message"));
    }, []);

    async function run(kind, fn) {
        setBusy(kind);
        try { await fn(); } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Something went wrong");
        }
        setBusy(null);
    }

    const save = () => run("save", async () => {
        const { data } = await api.put("/admin/sms-templates", { birthday: text });
        setCfg((c) => ({ ...c, birthday: data.birthday, is_custom: data.is_custom }));
        toast.success("Birthday message saved");
    });

    const sendTest = () => run("test", async () => {
        const { data } = await api.post("/admin/sms-templates/test", { birthday: text });
        toast.success(`Test text sent to ${data.to}`);
    });

    if (!cfg) return null;

    const preview = text.split(TOKEN).join(firstName(user));
    const dirty = text.trim() !== cfg.birthday;
    const tooLong = text.length > cfg.max_length;
    const empty = !text.trim();

    return (
        <div className="rounded-xl border border-border p-4 mb-4 space-y-3" data-testid="birthday-sms-editor">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold flex items-center gap-2"><Cake className="h-4 w-4" />Birthday text message</div>
                    <div className="text-xs text-muted-foreground">Use <code className="font-mono bg-muted px-1 rounded">{TOKEN}</code> to insert each member's first name.</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${cfg.is_custom ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"}`} data-testid="birthday-sms-status">
                    {cfg.is_custom ? "Custom" : "Default"}
                </span>
            </div>

            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="birthday-sms-textarea" />

            <div className="flex items-center justify-between text-xs">
                <Button type="button" size="sm" variant="ghost" className="h-7 px-2 rounded-full" onClick={() => setText((t) => `${t}${t && !t.endsWith(" ") ? " " : ""}${TOKEN}`)} data-testid="birthday-sms-insert-name">
                    + Insert first name
                </Button>
                <span className={tooLong ? "text-red-600 font-semibold" : "text-muted-foreground"} data-testid="birthday-sms-count">
                    {text.length}/{cfg.max_length}
                </span>
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="birthday-sms-preview">
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Preview</div>
                <div className="text-sm whitespace-pre-wrap">{preview || <span className="text-muted-foreground">Nothing to preview yet</span>}</div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" className="rounded-full" disabled={!!busy || !dirty || tooLong || empty} onClick={save} data-testid="birthday-sms-save">
                    {busy === "save" ? "Saving…" : "Save message"}
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy || tooLong || empty} onClick={sendTest} data-testid="birthday-sms-test">
                    <Send className="h-3.5 w-3.5 mr-1" />{busy === "test" ? "Sending…" : "Send test to my phone"}
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full" disabled={!!busy || text === cfg.default_birthday} onClick={() => setText(cfg.default_birthday)} data-testid="birthday-sms-reset">
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />Use default wording
                </Button>
            </div>
        </div>
    );
}
